import { DataExplorerViewCollection } from './DataExplorerViewCollection';
import type {
  DataExplorerViewDefinition,
  DataExplorerViewId,
  DataExplorerViewProps,
  DataExplorerViewRenderProps,
} from './types';

function resolveActiveViewDefinition<
  TItem,
  TContext,
  TViewId extends DataExplorerViewId,
>(
  views: Array<DataExplorerViewDefinition<TItem, TContext, TViewId>>,
  activeViewId?: TViewId,
  fallbackViewId?: TViewId,
) {
  const visibleViews = views.filter((view) => !view.hidden);
  if (visibleViews.length === 0) {
    return undefined;
  }

  if (activeViewId) {
    const matched = visibleViews.find((view) => view.id === activeViewId);
    if (matched) {
      return matched;
    }
  }

  if (fallbackViewId) {
    const fallback = visibleViews.find((view) => view.id === fallbackViewId);
    if (fallback) {
      return fallback;
    }
  }

  return visibleViews[0];
}

export function DataExplorerView<
  TItem = unknown,
  TContext = unknown,
  TViewId extends DataExplorerViewId = DataExplorerViewId,
>({
  items,
  views = [],
  activeViewId,
  fallbackViewId,
  context,
  capabilities,
  className,
  classNames,
  empty,
  loading,
  loadingNode,
  footer,
  overlay,
  layout,
  collection,
  selectedIds = [],
  getItemId,
  itemDefinition,
  renderItem,
  onOpenItem,
  onSelectionChange,
}: DataExplorerViewProps<TItem, TContext, TViewId>) {
  const resolvedItems = Array.isArray(items) ? items : [];
  const resolvedView = resolveActiveViewDefinition(views, activeViewId, fallbackViewId);
  const resolvedActiveViewId = (resolvedView?.id ?? activeViewId ?? fallbackViewId ?? '') as TViewId;
  const resolvedCollection =
    collection || resolvedView?.collection
      ? {
          ...collection,
          ...resolvedView?.collection,
          classNames: {
            ...collection?.classNames,
            ...resolvedView?.collection?.classNames,
          },
        }
      : undefined;
  const resolvedLayout = resolvedView?.layout ?? resolvedView?.collection?.layout ?? layout ?? collection?.layout;
  const resolvedLoading = resolvedView?.collection?.loading ?? collection?.loading ?? loading;
  const resolvedLoadingNode = resolvedView?.collection?.loadingNode ?? collection?.loadingNode ?? loadingNode;
  const resolvedFooter = resolvedView?.collection?.footer ?? collection?.footer ?? footer;
  const resolvedOverlay = resolvedView?.collection?.overlay ?? collection?.overlay ?? overlay;
  const resolvedEmpty =
    typeof resolvedView?.empty === 'function'
      ? resolvedView.empty({
          items: resolvedItems,
          activeViewId: resolvedActiveViewId,
          context,
          capabilities,
          className,
          empty,
          loading,
          loadingNode,
          footer,
          overlay,
          layout,
          collection: resolvedCollection,
          selectedIds,
          getItemId,
          itemDefinition,
          renderItem,
          onOpenItem,
          onSelectionChange,
        })
      : resolvedView?.empty ?? empty;

  const renderProps: DataExplorerViewRenderProps<TItem, TContext, TViewId> = {
    items: resolvedItems,
    activeViewId: resolvedActiveViewId,
    context,
    capabilities,
    className,
    empty: resolvedEmpty,
    loading: resolvedLoading,
    loadingNode: resolvedLoadingNode,
    footer: resolvedFooter,
    overlay: resolvedOverlay,
    layout: resolvedLayout,
    collection: resolvedCollection,
    selectedIds,
    getItemId,
    itemDefinition: resolvedView?.itemDefinition ?? itemDefinition,
    renderItem: resolvedView?.renderItem ?? renderItem,
    onOpenItem,
    onSelectionChange,
  };

  if (resolvedView?.render) {
    return <>{resolvedView.render(renderProps)}</>;
  }

  return (
    <DataExplorerViewCollection<TItem, TContext, TViewId>
      items={resolvedItems}
      activeViewId={resolvedActiveViewId}
      context={context}
      capabilities={capabilities}
      className={resolvedCollection?.className ?? className}
      classNames={{
        ...classNames,
        ...resolvedCollection?.classNames,
      }}
      empty={resolvedEmpty}
      loading={renderProps.loading}
      loadingNode={renderProps.loadingNode}
      footer={renderProps.footer}
      overlay={renderProps.overlay}
      layout={renderProps.layout}
      scrollable={resolvedCollection?.scrollable}
      wrapItems={resolvedCollection?.wrapItems}
      measuredGrid={resolvedCollection?.measuredGrid}
      itemsStyle={resolvedCollection?.itemsStyle}
      itemStyle={resolvedCollection?.itemStyle}
      selectedIds={selectedIds}
      getItemId={getItemId}
      itemDefinition={renderProps.itemDefinition}
      renderItem={renderProps.renderItem}
      getItemClassName={resolvedView?.getItemClassName}
      onOpenItem={onOpenItem}
      onSelectionChange={onSelectionChange}
    />
  );
}
